
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AttendanceReports = () => {
  const [activeReport, setActiveReport] = useState('employee');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [companies, setCompanies] = useState([{ id: 'all', name: 'All Companies' }]);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [departmentStats, setDepartmentStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDeptEmployees, setSelectedDeptEmployees] = useState([]);
  const [isDeptModalOpen, setIsDeptModalOpen] = useState(false);
  const [selectedDeptName, setSelectedDeptName] = useState('');

  useEffect(() => {
    const fetchCompanyAndData = async () => {
      const companyId = localStorage.getItem('selectedCompanyId');
      const companyName = localStorage.getItem('selectedCompanyName') || 'Your Company';
      
      if (companyId) {
        setCompanies([{ id: companyId, name: companyName }]);
        setSelectedCompany(companyId);
        await Promise.all([
          fetchEmployees(companyId),
          fetchDepartments(companyId),
          fetchAttendanceData(companyId)
        ]);
      } else {
        setCompanies([{ id: 'all', name: 'All Companies' }]);
        setLoading(false);
      }
    };

    fetchCompanyAndData();
  }, []);

  useEffect(() => {
    if (activeReport === 'department' && departments.length > 0) {
      fetchDepartmentStats();
    }
  }, [activeReport, departments]);

  const fetchEmployees = async (companyId) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('No access token found. Please log in.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `https://customersupport-jhy9.onrender.com/api/employees/company/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmployees([
        { _id: 'all', teamMemberName: 'All Employees', department: 'all' },
        ...response.data
      ]);
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError(`Failed to fetch employees: ${err.response?.data?.message || err.message}`);
    }
  };

  const fetchDepartments = async (companyId) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('No access token found. Please log in.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `https://customersupport-jhy9.onrender.com/api/departments/company/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDepartments([
        { _id: 'all', name: 'All Departments' },
        ...response.data
      ]);
    } catch (err) {
      console.error('Error fetching departments:', err);
      setError(`Failed to fetch departments: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartmentStats = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('No access token found. Please log in.');
      return;
    }

    try {
      // TODO: Implement API call to fetch actual department attendance statistics
      // For now, initialize with empty stats until real API is implemented
      const stats = departments
        .filter(dept => dept._id !== 'all')
        .map((dept) => {
          // Count actual employees in this department
          const employeeCount = employees.filter(emp => 
            emp.department?._id === dept._id || emp.department === dept._id
          ).length;
          
          return {
            _id: dept._id,
            name: dept.departmentName || dept.name,
            present: 0,
            absent: 0,
            late: 0,
            employees: employeeCount
          };
        });
      
      setDepartmentStats(stats);
    } catch (err) {
      console.error('Error fetching department stats:', err);
      setError(`Failed to fetch department stats: ${err.response?.data?.message || err.message}`);
    }
  };

  // State for attendance data
  const [attendanceData, setAttendanceData] = useState({
    employee: [],
    lateAbsent: [],
    workSummary: [],
    dailyAttendance: []
  });

  // API functions to fetch attendance data
  const fetchAttendanceData = async (companyId) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('No access token found. Please log in.');
      return;
    }

    try {
      // TODO: Replace with actual API endpoints when available
      // For now, these will return empty arrays until the backend APIs are implemented
      
      // Fetch employee attendance summary
      // const employeeResponse = await axios.get(
      //   `https://customersupport-jhy9.onrender.com/api/attendance/employee-summary/${companyId}`,
      //   { headers: { Authorization: `Bearer ${token}` } }
      // );

      // Fetch late/absent records
      // const lateAbsentResponse = await axios.get(
      //   `https://customersupport-jhy9.onrender.com/api/attendance/late-absent/${companyId}`,
      //   { headers: { Authorization: `Bearer ${token}` } }
      // );

      // Fetch work summary
      // const workSummaryResponse = await axios.get(
      //   `https://customersupport-jhy9.onrender.com/api/attendance/work-summary/${companyId}`,
      //   { headers: { Authorization: `Bearer ${token}` } }
      // );

      // Fetch daily attendance
      // const dailyAttendanceResponse = await axios.get(
      //   `https://customersupport-jhy9.onrender.com/api/attendance/daily/${companyId}`,
      //   { headers: { Authorization: `Bearer ${token}` } }
      // );

      setAttendanceData({
        employee: [], // employeeResponse.data || []
        lateAbsent: [], // lateAbsentResponse.data || []
        workSummary: [], // workSummaryResponse.data || []
        dailyAttendance: [] // dailyAttendanceResponse.data || []
      });
    } catch (err) {
      console.error('Error fetching attendance data:', err);
      setError(`Failed to fetch attendance data: ${err.response?.data?.message || err.message}`);
    }
  };

  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  // Function to get department name by ID
  const getDepartmentName = (departmentId) => {
    if (departmentId === 'all') return 'All Departments';
    
    // Check if it's a real department from API
    const department = departments.find(dept => dept._id === departmentId);
    return department ? (department.departmentName || department.name) : 'N/A';
  };

  // Get employees filtered by selected department
  const filteredEmployeeData = employees.filter(emp => {
    // Handle department filter
    const matchesDepartment = selectedDepartment === 'all' || 
      emp.department?._id === selectedDepartment || 
      emp.department === selectedDepartment ||
      emp.department === 'all';
      
    // Handle employee filter
    const matchesEmployee = selectedEmployee === 'all' || 
      emp._id === selectedEmployee;
      
    return matchesDepartment && matchesEmployee;
  });

  const filteredLateAbsentData = attendanceData.lateAbsent.filter(item => {
    const matchesDepartment = selectedDepartment === 'all' || item.department === selectedDepartment;
    const matchesEmployee = selectedEmployee === 'all' || 
      (typeof item.name === 'string' && 
       employees.some(emp => emp._id === selectedEmployee && emp.teamMemberName === item.name));
    return matchesDepartment && matchesEmployee;
  });

  const filteredWorkSummaryData = attendanceData.workSummary
    .filter(item => {
      const matchesDepartment = selectedDepartment === 'all' || item.department === selectedDepartment;
      const matchesEmployee = selectedEmployee === 'all' || 
        (typeof item.name === 'string' && 
         employees.some(emp => emp._id === selectedEmployee && emp.teamMemberName === item.name));
      return matchesDepartment && 
             matchesEmployee &&
             (new Date(item.date).getFullYear() === selectedYear) &&
             (new Date(item.date).getMonth() === selectedMonth) &&
             (!selectedDate || item.date === selectedDate) &&
             (employeeSearch === '' || item.name.toLowerCase().includes(employeeSearch.toLowerCase()));
    });

  const filteredDailyAttendanceData = attendanceData.dailyAttendance
    .filter(item => {
      const matchesDepartment = selectedDepartment === 'all' || item.department === selectedDepartment;
      const matchesEmployee = selectedEmployee === 'all' || 
        (typeof item.name === 'string' && 
         employees.some(emp => emp._id === selectedEmployee && emp.teamMemberName === item.name));
      return matchesDepartment && 
             matchesEmployee &&
             (new Date(item.date).getFullYear() === selectedYear) &&
             (new Date(item.date).getMonth() === selectedMonth) &&
             (!selectedDate || item.date === selectedDate) &&
             (employeeSearch === '' || item.name.toLowerCase().includes(employeeSearch.toLowerCase()));
    });

  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRecord(null);
  };

  const closeDeptModal = () => {
    setIsDeptModalOpen(false);
    setSelectedDeptEmployees([]);
    setSelectedDeptName('');
  };

  const fetchEmployeesByDepartment = async (deptId, deptName) => {
    if (deptId === 'all') return;
    
    setLoading(true);
    const token = localStorage.getItem('authToken');
    
    try {
      // Filter existing employees by department
      const deptEmployees = employees.filter(emp => 
        (emp.department?._id === deptId || emp.department === deptId) && emp._id !== 'all'
      );
      
      setSelectedDeptEmployees(deptEmployees);
      setSelectedDeptName(deptName);
      setIsDeptModalOpen(true);
    } catch (err) {
      console.error('Error fetching department employees:', err);
      setError(`Failed to fetch department employees: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl shadow-lg p-6 mb-6 text-white">
          <h1 className="text-3xl font-bold mb-2">Attendance Reports</h1>
          <p className="text-blue-100">For managers/admins to analyze attendance patterns and trends</p>
        </div>

        {/* Report Type Selector */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6">
          <button
            onClick={() => setActiveReport('employee')}
            className={`p-4 rounded-xl shadow-md transition-all duration-200 flex flex-col items-center ${activeReport === 'employee' ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg transform scale-105' : 'bg-white text-gray-700 hover:shadow-lg'}`}
          >
            <div className="font-semibold text-sm md:text-base">Employee Report</div>
            <div className="text-xs mt-1 opacity-80">Filter by employee</div>
          </button>
          <button
            onClick={() => setActiveReport('department')}
            className={`p-4 rounded-xl shadow-md transition-all duration-200 flex flex-col items-center ${activeReport === 'department' ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg transform scale-105' : 'bg-white text-gray-700 hover:shadow-lg'}`}
          >
            <div className="font-semibold text-sm md:text-base">Department Report</div>
            <div className="text-xs mt-1 opacity-80">Summary by department</div>
          </button>
          <button
            onClick={() => setActiveReport('lateAbsent')}
            className={`p-4 rounded-xl shadow-md transition-all duration-200 flex flex-col items-center ${activeReport === 'lateAbsent' ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg transform scale-105' : 'bg-white text-gray-700 hover:shadow-lg'}`}
          >
            <div className="font-semibold text-sm md:text-base">Late/Absent</div>
            <div className="text-xs mt-1 opacity-80">Attendance issues</div>
          </button>
          <button
            onClick={() => setActiveReport('workSummary')}
            className={`p-4 rounded-xl shadow-md transition-all duration-200 flex flex-col items-center ${activeReport === 'workSummary' ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg transform scale-105' : 'bg-white text-gray-700 hover:shadow-lg'}`}
          >
            <div className="font-semibold text-sm md:text-base">Work Summary</div>
            <div className="text-xs mt-1 opacity-80">Daily work reports</div>
          </button>
          <button
            onClick={() => setActiveReport('dailyAttendance')}
            className={`p-4 rounded-xl shadow-md transition-all duration-200 flex flex-col items-center ${activeReport === 'dailyAttendance' ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg transform scale-105' : 'bg-white text-gray-700 hover:shadow-lg'}`}
          >
            <div className="font-semibold text-sm md-text-base">Daily Attendance</div>
            <div className="text-xs mt-1 opacity-80">Daily attendance by department</div>
          </button>
        </div>

        {/* Report Content Area */}
        <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">
          {/* Filters for relevant reports */}
          {(activeReport === 'employee' || activeReport === 'lateAbsent' || activeReport === 'workSummary' || activeReport === 'dailyAttendance') && (
            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label htmlFor="company-select" className="block text-sm font-medium text-gray-700 mb-1">Company:</label>
                <select
                  id="company-select"
                  value={selectedCompany}
                  disabled={companies.length <= 1}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 disabled:opacity-75 disabled:cursor-not-allowed"
                >
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="department-select" className="block text-sm font-medium text-gray-700 mb-1">Filter by Department:</label>
                <select
                  id="department-select"
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                >
                  {loading ? (
                    <option>Loading departments...</option>
                  ) : error ? (
                    <option>Error loading departments</option>
                  ) : (
                    departments.map(dept => (
                      <option key={dept._id || dept.id} value={dept._id || dept.id}>
                        {dept.departmentName || dept.name}
                      </option>
                    ))
                  )}
                </select>
              </div>
              
              {activeReport === 'employee' && (
                <div>
                  <label htmlFor="employee-select" className="block text-sm font-medium text-gray-700 mb-1">Filter by Employee:</label>
                  <select
                    id="employee-select"
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    disabled={loading}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 disabled:opacity-75 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <option>Loading employees...</option>
                    ) : error ? (
                    <option>Error loading employees</option>
                    ) : (
                      filteredEmployeeData.map(employee => (
                        <option key={employee._id} value={employee._id}>
                          {employee.teamMemberName || employee.name}
                          {employee.department?.name ? ` (${employee.department.name})` : ''}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              )}
              {(activeReport === 'workSummary' || activeReport === 'dailyAttendance') && (
                <>
                  <div>
                    <label htmlFor="year-select" className="block text-sm font-medium text-gray-700 mb-1">Select Year:</label>
                    <select
                      id="year-select"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                    >
                      {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="month-select" className="block text-sm font-medium text-gray-700 mb-1">Select Month:</label>
                    <select
                      id="month-select"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                    >
                      {months.map((month, index) => (
                        <option key={index} value={index}>{month}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="date-select" className="block text-sm font-medium text-gray-700 mb-1">Select Date:</label>
                    <input
                      id="date-select"
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                    />
                  </div>
                  <div>
                    <label htmlFor="employee-search" className="block text-sm font-medium text-gray-700 mb-1">Search Employee:</label>
                    <input
                      id="employee-search"
                      type="text"
                      value={employeeSearch}
                      onChange={(e) => setEmployeeSearch(e.target.value)}
                      placeholder="Search by employee name..."
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Employee-wise Report */}
          {activeReport === 'employee' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Employee-wise Attendance Report</h2>
              <div className="overflow-x-auto rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Employee</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Department</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Present</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Absent</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Late</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Attendance %</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredEmployeeData
                      .filter(emp => selectedEmployee === 'all' || emp._id === selectedEmployee)
                      .map(item => (
                        <tr key={item.id || item._id} className="hover:bg-blue-50 transition-colors">
                          <td className="px-4 py-4 whitespace-nowrap font-medium text-gray-900">{item.teamMemberName || item.name}</td>
                          <td className="px-4 py-4 whitespace-nowrap capitalize">
                            {item.departmentName || 
                             item.department?.name || 
                             getDepartmentName(item.department) || 
                             'N/A'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">{item.present || 0}</td>
                          <td className="px-4 py-4 whitespace-nowrap">{item.absent || 0}</td>
                          <td className="px-4 py-4 whitespace-nowrap">{item.late || 0}</td>
                          <td className="px-4 py-4 whitespace-nowrap font-semibold">
                            <span className={`px-3 py-1 rounded-full text-xs ${Math.round(((item.present || 0) / ((item.present || 0) + (item.absent || 0) || 1)) * 100) >= 90 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                              {Math.round(((item.present || 0) / ((item.present || 0) + (item.absent || 0) || 1)) * 100)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Department-wise Report */}
          {activeReport === 'department' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Department-wise Attendance Summary</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {departmentStats.length > 0 ? (
                  departmentStats.map(dept => (
                    <div key={dept._id} className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 shadow border border-blue-100 hover:shadow-md transition-shadow">
                      <h3 className="font-bold text-lg text-gray-800 mb-3 capitalize">{dept.name}</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Employees:</span>
                          <span className="font-semibold">{dept.employees}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Present:</span>
                          <span className="font-semibold text-green-600">{dept.present}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Absent:</span>
                          <span className="font-semibold text-red-600">{dept.absent}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Late Arrivals:</span>
                          <span className="font-semibold text-yellow-600">{dept.late}</span>
                        </div>
                        <button 
                          onClick={() => fetchEmployeesByDepartment(dept._id, dept.name)}
                          className="mt-3 w-full bg-blue-50 hover:bg-blue-100 text-blue-600 font-medium py-2 px-4 rounded-lg transition-colors text-sm"
                        >
                          View Employees
                        </button>
                        <div className="pt-2 mt-2 border-t border-blue-200">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Attendance Rate:</span>
                            <span className="font-semibold text-blue-600 text-lg">
                              {Math.round((dept.present / (dept.present + dept.absent)) * 100)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    {loading ? 'Loading department statistics...' : 'No department statistics available'}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Late/Absent Report */}
          {activeReport === 'lateAbsent' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Late and Absent Report</h2>
              <div className="overflow-x-auto rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Employee</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Department</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredLateAbsentData.map(item => (
                      <tr key={item.id} className="hover:bg-blue-50 transition-colors">
                        <td className="px-4 py-4 whitespace-nowrap font-medium text-gray-900">{item.name}</td>
                        <td className="px-4 py-4 whitespace-nowrap capitalize">{getDepartmentName(item.department)}</td>
                        <td className="px-4 py-4 whitespace-nowrap">{item.date}</td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${item.type === 'late' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleViewDetails(item)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1 bg-blue-50 rounded-lg transition-colors"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Modal for Late/Absent Details */}
          {isModalOpen && selectedRecord && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full mx-4">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Attendance Record Details</h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600 font-medium">Employee:</span>
                    <span className="text-gray-900">{selectedRecord.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 font-medium">Department:</span>
                    <span className="text-gray-900 capitalize">{getDepartmentName(selectedRecord.department)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 font-medium">Date:</span>
                    <span className="text-gray-900">{selectedRecord.date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 font-medium">Status:</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${selectedRecord.type === 'late' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                      {selectedRecord.status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 font-medium">Reason:</span>
                    <span className="text-gray-900">{selectedRecord.reason || 'Not provided'}</span>
                  </div>
                  {/* Additional Department Stats */}
                  {departmentStats.find(dept => dept.name.toLowerCase() === selectedRecord.department) && (
                    <div className="border-t pt-4 mt-4">
                      <h4 className="text-lg font-semibold text-gray-800 mb-2">Department Summary</h4>
                      {(() => {
                        const dept = departmentStats.find(d => d.name.toLowerCase() === selectedRecord.department);
                        return (
                          <>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Total Employees:</span>
                              <span className="text-gray-900">{dept.employees}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Present Days:</span>
                              <span className="text-green-600">{dept.present}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Absent Days:</span>
                              <span className="text-red-600">{dept.absent}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Late Arrivals:</span>
                              <span className="text-yellow-600">{dept.late}</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={closeModal}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Work Report Summary */}
          {activeReport === 'workSummary' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Work Report Summary</h2>
              <div className="overflow-x-auto rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Employee</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Department</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Hours Worked</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Tasks Completed</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredWorkSummaryData.length > 0 ? (
                      filteredWorkSummaryData.map(item => (
                        <tr key={item.id} className="hover:bg-blue-50 transition-colors">
                          <td className="px-4 py-4 whitespace-nowrap font-medium text-gray-900">{item.name}</td>
                          <td className="px-4 py-4 whitespace-nowrap capitalize">{getDepartmentName(item.department)}</td>
                          <td className="px-4 py-4 whitespace-nowrap">{item.date}</td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${item.hours >= 8 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                              {item.hours} hrs
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm">{item.tasks}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-4 py-4 text-center text-gray-500">
                          No records found for {months[selectedMonth]} {selectedYear}
                          {selectedDate ? ` on ${selectedDate}` : ''}
                          {employeeSearch ? ` matching "${employeeSearch}"` : ''}
                          {selectedDepartment !== 'all' ? ` in ${getDepartmentName(selectedDepartment)}` : ''}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Daily Attendance Report */}
          {activeReport === 'dailyAttendance' && (
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Daily Attendance Report</h2>
              <div className="overflow-x-auto rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Employee</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Department</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredDailyAttendanceData.length > 0 ? (
                      filteredDailyAttendanceData.map(item => (
                        <tr key={item.id} className="hover:bg-blue-50 transition-colors">
                          <td className="px-4 py-4 whitespace-nowrap font-medium text-gray-900">{item.name}</td>
                          <td className="px-4 py-4 whitespace-nowrap capitalize">{getDepartmentName(item.department)}</td>
                          <td className="px-4 py-4 whitespace-nowrap">{item.date}</td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              item.status === 'Present' ? 'bg-green-100 text-green-800' :
                              item.status.includes('Late') ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {item.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="px-4 py-4 text-center text-gray-500">
                          No attendance records found for {months[selectedMonth]} {selectedYear}
                          {selectedDate ? ` on ${selectedDate}` : ''}
                          {employeeSearch ? ` matching "${employeeSearch}"` : ''}
                          {selectedDepartment !== 'all' ? ` in ${getDepartmentName(selectedDepartment)}` : ''}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Department Employees Modal */}
        {isDeptModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">
                  Employees in {selectedDeptName}
                </h3>
                <button 
                  onClick={closeDeptModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1">
                {loading ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : selectedDeptEmployees.length > 0 ? (
                  <div className="space-y-4">
                    {selectedDeptEmployees.map(emp => (
                      <div key={emp._id} className="bg-gray-50 p-4 rounded-lg flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{emp.teamMemberName || emp.name}</h4>
                          <p className="text-sm text-gray-500">{emp.email || 'No email provided'}</p>
                        </div>
                        <span className="text-sm px-3 py-1 bg-blue-100 text-blue-800 rounded-full">
                          {emp.designation || 'Employee'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No employees found in this department.</p>
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={closeDeptModal}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceReports;